import React from 'react';
import styled from 'styled-components';
import { StoryComment } from '../shared/types';

const CommentsContainer = styled.div`
  padding: 2rem;
  background: var(--color-gray-50);
  border-top: 1px solid var(--color-gray-200);
`;

const CommentsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const CommentsTitle = styled.h3`
  font-family: var(--font-serif);
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-gray-900);
  margin: 0;
`;

const CommentCount = styled.span`
  color: var(--color-gray-600);
  font-size: 0.9rem;
  font-weight: 500;
`;

const CommentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CommentCard = styled.div`
  background: var(--color-white);
  border-radius: var(--border-radius-lg);
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--color-gray-200);
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const CommentAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AuthorName = styled.span`
  font-weight: 600;
  color: var(--color-gray-900);
  font-size: 0.9rem;
`;

const AuthorUsername = styled.span`
  color: var(--color-gray-500);
  font-size: 0.8rem;
  font-weight: 500;
`;

const CommentDate = styled.span`
  color: var(--color-gray-500);
  font-size: 0.8rem;
  font-weight: 500;
`;

const CommentContent = styled.p`
  color: var(--color-gray-700);
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--color-gray-500);
`;

const EmptyIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1rem;
`;

const EmptyTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-gray-600);
  margin: 0 0 0.5rem 0;
`;

const EmptyText = styled.p`
  font-size: 0.9rem;
  margin: 0;
`;

const AddCommentButton = styled.button`
  background: var(--color-primary);
  color: var(--color-white);
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: var(--border-radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  
  &:hover:not(:disabled) {
    background: var(--color-primary-dark);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

interface CommentsSectionProps {
  comments: StoryComment[];
  onAddComment: () => void;
  canComment: boolean;
  loading: boolean;
}

const CommentsSection: React.FC<CommentsSectionProps> = ({
  comments,
  onAddComment,
  canComment,
  loading
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
    });
  };

  return (
    <CommentsContainer>
      <CommentsHeader>
        <CommentsTitle>Comments</CommentsTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <CommentCount>
            {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
          </CommentCount>
          {canComment && (
            <AddCommentButton onClick={onAddComment} disabled={loading}>
              💬 Add Comment
            </AddCommentButton>
          )}
        </div>
      </CommentsHeader>
      
      {comments.length === 0 ? (
        <EmptyState>
          <EmptyIcon>💬</EmptyIcon>
          <EmptyTitle>No comments yet</EmptyTitle>
          <EmptyText>
            {canComment 
              ? "Be the first to share your thoughts about this story!" 
              : "This story doesn't have any comments yet."}
          </EmptyText>
        </EmptyState>
      ) : (
        <CommentsList>
          {comments.map((comment) => (
            <CommentCard key={comment.id}>
              <CommentHeader>
                <CommentAuthor>
                  👤
                  <AuthorName>{comment.userFullName}</AuthorName>
                  <AuthorUsername>@{comment.username}</AuthorUsername>
                </CommentAuthor>
                <CommentDate>{formatDate(comment.createdAt)}</CommentDate>
              </CommentHeader>
              <CommentContent>{comment.content}</CommentContent>
            </CommentCard>
          ))}
        </CommentsList>
      )}
    </CommentsContainer>
  );
};

export default CommentsSection;